import { useCallback, useMemo } from 'react';

import cloneDeep from 'lodash-es/cloneDeep';
import { AlertTriangleIcon, CirclePlusIcon, InfoIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import useDialogAlert from '@/hooks/useDialogAlert';
import { generateDefaultCarouselCard, randId } from '@/redux/dialogs/helper';
import { selectCarouselActionData, selectPassthrough } from '@/redux/dialogs/selectors';
import { selectFeatureFlags } from '@/redux/featureFlag/selectors';
import { addCarouselCard, updateCardButtons, updateCarouselCards } from '@/redux/nodes/actions';
import { selectSelectedCarouselIndex } from '@/redux/nodes/selectors';
import { CarouselCard } from '@/shared/types/action';
import { noop } from '@/util/util';

import CarouselAccordion from './CarouselAccordion';
import { MAX_CAROUSEL_CARDS } from './constants';
import { move } from '../../helper';
import Passthrough from '../../Passthrough';
import ToolkitWrapper from '../../ToolkitWrapper';

export type CarouselFormType = { carousel: CarouselCard[] };

const ToolkitActionCarousel = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { passthrough: isPassthroughEnabled } = useSelector(selectFeatureFlags);

  const selectedCarouselIndex = useSelector(selectSelectedCarouselIndex);
  const { actionId, cards } = useSelector(selectCarouselActionData);
  const passthrough = useSelector(selectPassthrough);

  // Add a unique id to every button in the card
  const cardsWithButtonId = useMemo(
    () =>
      cards.map((card) => ({
        ...card,
        buttons: card?.buttons?.map((button) => ({ ...button, id: button.id || randId(6) })),
      })),
    [cards]
  );

  const showErrorBanner = cards.length === 0;
  const hasReachedMaxCards = cards.length === MAX_CAROUSEL_CARDS;

  const createNewCard = useCallback(() => {
    const newCard = generateDefaultCarouselCard(cards.length + 1);

    // Update redux
    dispatch(
      addCarouselCard({ direction: 'end', currentIndex: selectedCarouselIndex, actionId, newCard })
    );
  }, [actionId, cards.length, dispatch, selectedCarouselIndex]);

  const addCarouselButton = useCallback(
    (index: number) => {
      const randomId = randId(6);
      const buttons = [
        ...cardsWithButtonId[index].buttons,
        {
          type: 'postback',
          value: t('dialog.carousel.rand_text', { 0: randomId }),
          label: `${t('dialog.carousel.button')} ${cardsWithButtonId[index].buttons.length + 1}`,
          id: randomId,
        },
      ];

      dispatch(updateCardButtons({ actionId, cardIndex: index, buttons }));
    },
    [actionId, cardsWithButtonId, dispatch, t]
  );

  const handleDragCard = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const reOrderedCards = move(cloneDeep(cards as CarouselCard[]), dragIndex, hoverIndex);

      dispatch(updateCarouselCards({ actionId, cards: cloneDeep(reOrderedCards) }));
    },
    [actionId, cards, dispatch]
  );

  const { setFieldError: updateErrors } = useDialogAlert({
    id: actionId,
    nodeId: actionId,
    title: t('dialog.carousel.validation_error'),
    type: 'carousel',
  });

  return (
    <ToolkitWrapper type="carousel">
      <div className="pb-2 text-center">
        <Button size="sm" variant="ghost" onClick={createNewCard} disabled={hasReachedMaxCards}>
          <CirclePlusIcon
            color={
              hasReachedMaxCards ? 'var(--color-foreground-muted)' : 'var(--color-foreground-muted)'
            }
            size={16}
          />
          {t('common.add_card')}
        </Button>
      </div>

      {showErrorBanner && (
        <div className="mt-6">
          <Alert variant="destructive">
            <AlertTriangleIcon size={16} />
            <AlertDescription>
              {t('dialog.errors.at_least_one', { 0: t('actions.types.card').toLowerCase() })}
            </AlertDescription>
          </Alert>
        </div>
      )}

      <div className="flex flex-col justify-center gap-2 [&>div:last-child]:mb-16">
        {cardsWithButtonId.map((card, index) => (
          <CarouselAccordion
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            index={index}
            card={card}
            handleAddButton={() => addCarouselButton(index)}
            actionId={actionId}
            moveItem={handleDragCard}
            dropItem={noop}
            updateErrors={updateErrors}
          />
        ))}

        {hasReachedMaxCards && (
          <Alert variant="info">
            <InfoIcon size={16} />
            <AlertDescription>{t('dialog.carousel.max_cards')}</AlertDescription>
          </Alert>
        )}
      </div>

      {isPassthroughEnabled && <Passthrough actionId={actionId} passthrough={passthrough} />}
    </ToolkitWrapper>
  );
};

export default ToolkitActionCarousel;
